import * as React from 'react'
import { Component, ReactNode } from 'react'
// Utilisation des icônes Carbon officielles
import { WarningAltFilled, Restart, Home } from '@carbon/icons-react'
import {
  Button,
  Tile,
  Stack,
  ExpandableTile,
  TileAboveTheFoldContent,
  TileBelowTheFoldContent,
  Theme,
} from '@carbon/react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ error, errorInfo })
    this.props.onError?.(error, errorInfo)
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  handleGoHome = (): void => {
    window.location.href = '/'
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <Theme theme="g100">
          {' '}
          {/* Optionnel: Forcer un thème pour l'écran d'erreur */}
          <div
            style={{
              display: 'flex',
              minHeight: '100vh',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--cds-background)',
              padding: '1rem',
            }}
          >
            <Tile style={{ width: '100%', maxWidth: '600px' }}>
              <Stack gap={7}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
                >
                  <WarningAltFilled
                    size={32}
                    color="var(--cds-support-error)"
                  />
                  <h2 style={{ fontSize: 'var(--cds-productive-heading-04)' }}>
                    Une erreur est survenue
                  </h2>
                </div>

                <p style={{ color: 'var(--cds-text-secondary)' }}>
                  L'application a rencontré une erreur inattendue. Nos équipes
                  ont été notifiées et travaillent sur une solution.
                </p>

                {/* Détails techniques avec ExpandableTile de Carbon */}
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <ExpandableTile>
                    <TileAboveTheFoldContent>
                      <div
                        style={{
                          color: 'var(--cds-support-error)',
                          fontWeight: 600,
                        }}
                      >
                        {this.state.error.name}: {this.state.error.message}
                      </div>
                    </TileAboveTheFoldContent>
                    <TileBelowTheFoldContent>
                      <pre
                        style={{
                          marginTop: '1rem',
                          padding: '1rem',
                          backgroundColor: 'var(--cds-layer-02)',
                          overflowX: 'auto',
                          fontSize: '0.75rem',
                        }}
                      >
                        {this.state.errorInfo?.componentStack}
                      </pre>
                    </TileBelowTheFoldContent>
                  </ExpandableTile>
                )}

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <Button
                    onClick={this.handleReset}
                    kind="primary"
                    renderIcon={Restart}
                  >
                    Réessayer
                  </Button>
                  <Button
                    onClick={this.handleGoHome}
                    kind="secondary"
                    renderIcon={Home}
                  >
                    Accueil
                  </Button>
                </div>
              </Stack>
            </Tile>
          </div>
        </Theme>
      )
    }

    return this.props.children
  }
}

export function useErrorHandler(): (error: Error) => void {
  return (error: Error) => {
    throw error
  }
}
